import React, { useEffect, useState } from "react";
import { Star, MessageCircle } from "lucide-react";
import { listReviews, postReview } from "@/lib/userAuth";
import { toast } from "sonner";
import { SectionTitle } from "@/components/Section";
import { PhoneOtpVerify } from "@/components/PhoneOtpVerify";

export const StarRating = ({ value, onChange, size = 20 }) => (
  <div className="flex gap-0.5" data-testid="star-rating">
    {[1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        onClick={() => onChange?.(n)}
        disabled={!onChange}
        data-testid={onChange ? `star-${n}` : undefined}
        className={onChange ? "cursor-pointer" : "cursor-default"}
      >
        <Star size={size} className={n <= value ? "text-[#F59E0B] fill-[#F59E0B]" : "text-[#E5D3DA]"} />
      </button>
    ))}
  </div>
);

const ReviewCard = ({ review }) => (
  <div data-testid={`review-${review.id}`} className="bg-white rounded-2xl p-5 border border-[#F4D8E2] shadow-[0_4px_20px_rgba(216,27,96,0.04)]">
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F8BBD0] to-[#D81B60] flex items-center justify-center text-white font-serif">
          {review.name?.[0]?.toUpperCase() || "S"}
        </div>
        <div>
          <p className="text-sm font-semibold text-[#2D0A22]">{review.name}</p>
          <p className="text-[10px] text-[#8E6377]">{new Date(review.created_at).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })}</p>
        </div>
      </div>
      <StarRating value={review.rating} size={14} />
    </div>
    <p className="text-sm text-[#2D0A22] leading-relaxed">{review.text}</p>
  </div>
);

export const ReviewsSection = ({ limit = 4, showWriteButton = true }) => {
  const [reviews, setReviews] = useState([]);
  const [writing, setWriting] = useState(false);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => listReviews({ limit }).then(setReviews).catch(() => {});
  useEffect(() => { load(); }, [limit]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    if (!phoneVerified) { toast.error("Please verify your phone via OTP"); return; }
    if (!text.trim()) { toast.error("Please write a short review"); return; }
    setSaving(true);
    try {
      await postReview({ rating, text, name });
      toast.success("Thank you for your review 🌸");
      setText(""); setName(""); setPhone(""); setPhoneVerified(false); setRating(5); setWriting(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save review");
    } finally { setSaving(false); }
  };

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="reviews-section">
      <SectionTitle
        eyebrow="Loved by 20K+"
        title="What Customers Say"
        action="See all reviews"
        actionTo="/reviews"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {reviews.slice(0, limit).map(r => <ReviewCard key={r.id} review={r} />)}
      </div>
      {showWriteButton && (
        <div className="mt-6 text-center">
          {!writing ? (
            <button data-testid="write-review-button" onClick={() => setWriting(true)} className="pill-btn inline-flex items-center gap-2">
              <MessageCircle size={16} /> Write a Review
            </button>
          ) : (
            <div className="max-w-md mx-auto p-6 bg-white rounded-3xl border border-[#F4D8E2] text-left" data-testid="write-review-form">
              <p className="font-serif text-lg text-[#2D0A22]">Share your experience</p>
              <input data-testid="review-name" value={name} onChange={e => setName(e.target.value)} placeholder="Your Name" className="w-full mt-3 px-3 py-2 rounded-xl border border-[#F4D8E2] outline-none focus:border-[#D81B60]" />
              <div className="mt-2">
                <PhoneOtpVerify
                  phone={phone} onPhoneChange={setPhone}
                  verified={phoneVerified}
                  onVerified={(p) => { setPhone(p); setPhoneVerified(true); }}
                  name={name}
                />
              </div>
              <div className="mt-3"><StarRating value={rating} onChange={setRating} size={26} /></div>
              <textarea data-testid="review-text" value={text} onChange={e => setText(e.target.value)} rows={4} maxLength={500}
                placeholder="Tell us about your bouquet..."
                className="w-full mt-3 px-3 py-2 rounded-xl border border-[#F4D8E2] outline-none focus:border-[#D81B60] resize-none" />
              <div className="flex gap-2 mt-3">
                <button onClick={() => setWriting(false)} className="flex-1 py-2 rounded-xl border border-[#F4D8E2] text-[#8E6377]">Cancel</button>
                <button data-testid="submit-review-button" onClick={submit} disabled={saving} className="flex-1 pill-btn">{saving ? "Saving..." : "Post Review"}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  useEffect(() => { listReviews({ limit: 100 }).then(setReviews); }, []);
  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : "5.0";
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 pb-24 md:pb-16" data-testid="reviews-page">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FCE4EC] rounded-full text-[#D81B60] text-xs font-bold">
          <Star size={12} fill="currentColor" /> {avg} · {reviews.length} reviews
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#2D0A22] mt-3">Customer Reviews</h1>
        <p className="text-[#8E6377] mt-2">Real stories from Sugandhi Flowers customers.</p>
      </div>
      <div className="space-y-3">
        {reviews.map(r => <ReviewCard key={r.id} review={r} />)}
      </div>
    </div>
  );
};
